import React, { useEffect, useCallback, useContext } from 'react';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import Image from '../../Image';
import imageSizes from './imageSizes';
import { breakpoints } from '../../../config/constants';
import gtmPush from '../../../helpers/gtmPush';
import decodeGlobalContext from '../../../redux/decodeGlobalContext';

import './styles.scss';

const HomepageCardClick = (contentTitle, contentCategory, posterBlockType) =>
  gtmPush({
    event: 'DECODE article card click',
    contentTitle,
    contentCategory,
    posterType: posterBlockType
  });

const PosterLinkWrapper = ({ id, children, isLinkInternal, link, ...rest }) =>
  isLinkInternal ? (
    <Link to={`/${link}`} {...rest} id={id}>
      {children}
    </Link>
  ) : (
    <a href={link} {...rest} target="_blank" rel="noreferrer">
      {children}
    </a>
  );

const PosterComponent = ({ setType, posterData, isFilterPage }) => {
  const isTabletOrDesktop = window.innerWidth >= breakpoints.tablet;
  let posterCounter = 1;
  let gridRowCounter = 1;

  // Ensure the order follows the sequence of sortOrder (ascending).
  posterData.sort((a, b) => a.sortOrder - b.sortOrder);

  const allPosters = posterData.map((poster, posterIndex) => {
    const {
      posterBlockType,
      posterBreakerOrientation,
      contentTitle,
      contentAssets,
      posterRatio,
      offsetSettings,
      linkOut,
      blendMode
    } = poster;

    // Just don't display the card if there's no linkout data.
    if (!linkOut || !linkOut.length) return null;

    const isArticleBreaker = posterBlockType === 'featuredArticleBreaker';
    const imageSize = imageSizes[setType] && imageSizes[setType][posterIndex];
    const articleData = linkOut[0].contentAsset;
    const ctaText = linkOut[0].ctaText || '';
    const header = linkOut[0].externalHeader || '';
    const paragraph = linkOut[0].paragraph || '';

    let categories = [];
    let articleSlug = linkOut[0].ctaUrl || '';
    let postDate = linkOut[0].dateCreated;
    let offsetClass = '';
    let linkOutExternalClasses = '';
    let contentCategory = '';
    let slugID = '';

    const isLinkInternal = articleData && articleData.length;

    if (isLinkInternal) {
      categories = articleData[0].category;
      articleSlug = articleData[0].uri;
      slugID = articleData[0].uri.split('/').pop();
      postDate = articleData[0].postDate;
      contentCategory = categories.length && categories[0].title;
    }

    const dateToDisplay = new Date(postDate);
    const dateString = dateToDisplay
      .toLocaleString('dfault', {
        month: 'long',
        year: 'numeric',
        day: 'numeric'
      })
      .split(' ');
    const dateToDisplayString = `${dateString[1].replace(',', '')} ${dateString[0]} ${
      dateString[2]
    }`;
    const ratioValue = isArticleBreaker ? 'breaker' : posterRatio.split('').join('-');

    // Select specific large card areas per set.
    const isLargeCardArea =
      (!isArticleBreaker && setType === 'setType1' && (posterIndex === 0 || posterIndex === 5)) ||
      (setType === 'setType2' && (posterIndex === 1 || posterIndex === 6));

    // Condition to select specifc cards to consume whole row (no overlap)
    const isSetTwoFifthCard = !isArticleBreaker && setType === 'setType2' && posterIndex === 4;
    const isSetTwoLastCard = !isArticleBreaker && setType === 'setType2' && posterIndex === 6;

    const blendModeClass = blendMode && blendMode === 'on' ? 'has-blend-mode' : '';

    // Add a custom asset class for LinkOut specific to the large Card in Set 2
    // and the gradient background if there's a paragraph
    const customLinkOutImageClass = classNames('homepage-card__link-out-image-area', {
      'homepage-card__link-out-image-area--extended': isLargeCardArea && !isLinkInternal,
      'homepage-card__link-out-image-area--gradient': paragraph.length !== 0
    });

    if (isLinkInternal) {
      let offsetText = offsetSettings === 'topRight' ? 'top-right' : offsetSettings;

      offsetText = isArticleBreaker ? 'left' : offsetText;

      offsetClass = `homepage-card__item--offset-${offsetText}`;
    } else {
      linkOutExternalClasses = 'homepage-card__item--external';

      if (contentAssets.length > 1) {
        linkOutExternalClasses = 'homepage-card__item--external-with-asset';
      }
    }

    const cardItemClasses = ['homepage-card__item']
      .concat(linkOutExternalClasses.length ? linkOutExternalClasses : [])
      .concat(offsetClass.length ? offsetClass : [])
      .concat(!isArticleBreaker ? `homepage-card__item--ratio-${ratioValue}` : [])
      .concat(isArticleBreaker ? `homepage-card__item--breaker` : [])
      .concat(isArticleBreaker ? `homepage-card__item--${posterBreakerOrientation}` : [])
      .concat(!isArticleBreaker ? `homepage-card__item--ratio-${ratioValue}` : [])
      .concat(!isArticleBreaker ? `card-${posterCounter}` : [])
      .join(' ');

    if (!isArticleBreaker) {
      posterCounter++;
    }

    if (posterIndex > 0 && isArticleBreaker) {
      gridRowCounter++;
    }

    // Set the row arrangement of posters/breaker.
    const rowStyle = isTabletOrDesktop ? `${gridRowCounter}/span 3` : '';

    gridRowCounter += isArticleBreaker ? 3 : 2;

    // Set specific row placing of 2nd set - 7th card.
    // Apply only if the previous card from it is not a breaker.
    if (isSetTwoFifthCard) {
      gridRowCounter++;
    }

    // Set specific row placing of 2nd set - 7th card.
    // Apply only if the previous card from it is not a breaker.
    if (isSetTwoLastCard) {
      gridRowCounter -= 2;
    }

    return (
      <div key={posterIndex} className={cardItemClasses} style={{ gridRow: rowStyle }}>
        <div className="homepage-card__contents clearfix">
          <p className="homepage-card__content-category">
            {!isFilterPage && categories && categories.length ? (
              categories.map((category, categIndex) => (
                <a key={categIndex} href={`/decode?category=${category.slug}`}>
                  <span key={`category-${categIndex}`}>{category.title}</span>
                  <span>&nbsp;&mdash;&nbsp;</span>
                  <span className="homepage-card__content-date">{dateToDisplayString}</span>
                </a>
              ))
            ) : (
              <span className="homepage-card__content-date">{dateToDisplayString}</span>
            )}
          </p>
          {isArticleBreaker ? (
            <PosterLinkWrapper
              title={contentTitle}
              aria-label={contentTitle}
              isLinkInternal={isLinkInternal}
              link={articleSlug}
              id={slugID}
              onClick={() => HomepageCardClick(contentTitle, contentCategory, posterBlockType)}
              className="homepage-card__item-title-link"
              key={`poster-content-${posterIndex}`}>
              <p className="homepage-card__content-title">{contentTitle}</p>
            </PosterLinkWrapper>
          ) : null}
        </div>
        <PosterLinkWrapper
          title={contentTitle}
          aria-label={contentTitle}
          isLinkInternal={isLinkInternal}
          link={articleSlug}
          id={`${slugID}-image`}
          className="homepage-card__item-asset-link"
          onClick={() => HomepageCardClick(contentTitle, contentCategory, posterBlockType)}
          key={`poster-asset-${posterIndex}`}>
          {!isArticleBreaker ? (
            <p className="homepage-card__content-title">{contentTitle}</p>
          ) : null}
          {isLinkInternal ? (
            <div className={`homepage-card__assets ${blendModeClass}`}>
              {contentAssets.map((asset, assetIndex) => (
                <Image
                  key={`image-layer-${assetIndex}`}
                  classes={`homepage-card__layer-${assetIndex + 1}`}
                  image={asset.url}
                  alt={asset.title}
                  sizes={imageSize}
                />
              ))}
            </div>
          ) : (
            <div className={`homepage-card__assets ${blendModeClass}`}>
              {contentAssets.length ? (
                <Image
                  classes="homepage-card__layer-3"
                  image={contentAssets[0].url}
                  alt={contentAssets[0].title}
                  sizes={imageSize}
                />
              ) : null}
              <div className="homepage-card__link-out-content">
                {contentAssets.length && contentAssets[1] ? (
                  <div className={customLinkOutImageClass}>
                    <div className="homepage-card__link-out-asset-wrapper">
                      <Image
                        classes="homepage-card__layer-1"
                        image={contentAssets[1].url}
                        alt={contentAssets[1].title}
                        sizes={imageSize}
                      />
                    </div>
                  </div>
                ) : null}
                <div
                  className="homepage-card__link-out-header"
                  dangerouslySetInnerHTML={{ __html: header }}
                />
                {paragraph.length ? (
                  <div
                    className="homepage-card__link-out-paragraph"
                    dangerouslySetInnerHTML={{ __html: paragraph }}
                  />
                ) : null}
                <span
                  className="homepage-card__link-out-cta"
                  dangerouslySetInnerHTML={{
                    __html: `
                    ${ctaText}
                    <span class="homepage-card__link-out-icon"></span>
                  `
                  }}
                />
              </div>
            </div>
          )}
        </PosterLinkWrapper>
      </div>
    );
  });

  return allPosters;
};

const HomepageCard = ({ categoryText, homepagePosters }) => {
  const filterPostersByCategory = useCallback(
    posters => {
      const filteredPostersArray = posters.map(set => {
        const { poster } = set;

        const updatedPosterArray = poster.filter(item => {
          const { linkOut } = item;

          if (linkOut.length && linkOut[0].contentAsset && linkOut[0].contentAsset.length) {
            const { contentAsset } = linkOut[0];
            const { category } = contentAsset[0];

            return category.length && category[0].slug === categoryText;
          }
          return false;
        });

        return {
          ...set,
          poster: updatedPosterArray
        };
      });

      return filteredPostersArray;
    },
    [categoryText]
  );

  const sortPostersFromLatest = useCallback(posters => {
    const sortedPostersArray = posters.map(set => {
      const { poster } = set;

      const updatedPosterArray = poster.sort((current, next) => {
        const [linkOutCurrent] = current.linkOut || [];
        const [linkOutNext] = next.linkOut || [];

        if (linkOutCurrent && linkOutNext) {
          const posterDateCurrent =
            linkOutCurrent.contentAsset && linkOutCurrent.contentAsset.length
              ? linkOutCurrent.contentAsset[0].postDate
              : linkOutCurrent.dateCreated;
          const posterDateNext =
            linkOutNext.contentAsset && linkOutNext.contentAsset.length
              ? linkOutNext.contentAsset[0].postDate
              : linkOutNext.dateCreated;

          if (posterDateCurrent > posterDateNext) return -1;
          if (posterDateCurrent < posterDateNext) return 1;

          return 0;
        }

        return 0;
      });

      return {
        ...set,
        poster: updatedPosterArray
      };
    });

    return sortedPostersArray;
  }, []);

  useEffect(() => {
    if (categoryText.length) {
      // Scroll back to top after a category filter.
      window.scrollTo(0, 0);
    }
  }, [categoryText]);

  const { state, dispatch } = useContext(decodeGlobalContext);
  const location = useLocation();
  const { previousArticleFocus } = state;

  useEffect(() => {
    if (previousArticleFocus) {
      const previousArticleImage = document.getElementById(`${previousArticleFocus}-image`);
      previousArticleImage?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    const focusPreviousArticle = e => {
      if (e.keyCode === 9) {
        if (previousArticleFocus) {
          document.getElementById(previousArticleFocus).focus();
          setTimeout(() => {
            dispatch({
              type: 'SET_PREVIOUS_ARTICLE_FOCUS',
              payload: ''
            });
          }, 100);
        }
      }
    };

    if (!location.pathname.includes('/article')) {
      window.addEventListener('keyup', focusPreviousArticle);
      return () => {
        window.removeEventListener('keyup', focusPreviousArticle);
      };
    }
  }, [state]);

  const updatedHomepagePosters = categoryText.length
    ? filterPostersByCategory(homepagePosters)
    : sortPostersFromLatest(homepagePosters);

  return (
    <div className="homepage-card">
      {updatedHomepagePosters.map((set, setIndex) => {
        const { setType, poster } = set;
        const setTypeClass = setType === 'setType1' ? 'type-1' : 'type-2';

        // Limit the first set to 6 posters only.
        if (setType === 'setType1' && poster.length > 6) {
          poster.pop();
        }

        // Remove posters with linkOut > contentAsset empty
        const filteredPoster = poster.filter(item => item?.linkOut[0]?.contentAsset.length > 0);

        return (
          <div
            key={`set-${setIndex}`}
            className={`homepage-card__set homepage-card__set--${setTypeClass}`}>
            {filteredPoster.length > 0 ? (
              <PosterComponent
                setType={setType}
                posterData={filteredPoster}
                isFilterPage={categoryText.length}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default HomepageCard;
